/**
 * skToast — the single toast system for the whole app (ported from sk-catalog).
 * ──────────────────────────────────────────────────────────────────────────
 * One place owns everything toast-related:
 *   • showToast(tone, text1, text2?)  — fire a toast from anywhere (handlers,
 *     services, screens). Tones map onto react-native-toast-message's
 *     success | error | info types.
 *   • <Toast/>                         — the host, mounted once in App.tsx. It
 *     carries `toastConfig`, which renders messages in a slightly larger,
 *     on-brand font with multi-line support so full backend messages show.
 *   • ToastProvider / useToast         — thin compatibility layer (kept so any
 *     `const toast = useToast()` call site keeps working); delegates to showToast.
 *
 * The successHandler / errorHandler call showToast() with the REAL backend
 * message, so every API result surfaces the server's own words.
 */
import React from 'react';
import { Platform } from 'react-native';
import RNToast, { BaseToast, ErrorToast, ToastConfig } from 'react-native-toast-message';
import { FontFamily } from '../theme/typography';

export type ToastTone = 'success' | 'info' | 'warn' | 'danger' | 'error';

// react-native-toast-message ships 'success' | 'error' | 'info' by default.
function mapType(tone: ToastTone): 'success' | 'error' | 'info' {
  if (tone === 'success') return 'success';
  if (tone === 'danger' || tone === 'error') return 'error';
  return 'info'; // info + warn
}

/* ── Visual config — slightly larger, branded text that wraps cleanly ── */
const text1Style = { fontSize: 16, fontWeight: '600' as const, fontFamily: FontFamily.sansSemibold, color: '#13161a' };
const text2Style = { fontSize: 14, fontWeight: '400' as const, fontFamily: FontFamily.sans, color: '#3e4348', lineHeight: 19 };
const baseStyle = (border: string) => ({ borderLeftColor: border, borderLeftWidth: 5, height: 'auto' as const, minHeight: 60, paddingVertical: 10, borderRadius: 10 });
const contentContainerStyle = { paddingHorizontal: 15, paddingVertical: 6 };

export const toastConfig: ToastConfig = {
  success: (props) => (
    <BaseToast {...props} style={baseStyle('#16a34a')} contentContainerStyle={contentContainerStyle} text1Style={text1Style} text2Style={text2Style} text2NumberOfLines={5} />
  ),
  info: (props) => (
    <BaseToast {...props} style={baseStyle('#2563eb')} contentContainerStyle={contentContainerStyle} text1Style={text1Style} text2Style={text2Style} text2NumberOfLines={5} />
  ),
  error: (props) => (
    <ErrorToast {...props} style={baseStyle('#dc2626')} contentContainerStyle={contentContainerStyle} text1Style={text1Style} text2Style={text2Style} text2NumberOfLines={5} />
  ),
};

/**
 * Fire a toast. text1 = headline, text2 = optional detail (e.g. the backend
 * message). Error toasts linger a touch longer so long messages can be read.
 */
export const showToast = (tone: ToastTone, text1: string, text2?: string): void => {
  RNToast.show({
    type: mapType(tone),
    text1,
    text2,
    position: 'top', // pin to the top — never let it slide off the bottom
    visibilityTime: tone === 'success' ? 3200 : 3800,
    autoHide: true,
    topOffset: 60,
    ...(text2 ? { text2NumberOfLines: 5 } : {}),
  });
};

/* ── Host — App.tsx renders this once at the root. ──────────────────────────
 * On web the toast must always sit at the TOP of the viewport and ABOVE open
 * modals. Relying on an in-tree RN <View position:'fixed'> is fragile: the host
 * is the last child of the app's flex column, so any layout/transform quirk can
 * anchor the (absolutely-positioned) toast to the bottom of the page — it then
 * renders below the fold. To make placement bullet-proof we portal the toast
 * host into a 0-height, viewport-fixed <div> appended to document.body, so its
 * top:0 anchor is the viewport top regardless of the app's layout. The div has
 * no height, so it never blocks clicks except where the toast actually paints.
 */
const WebToastHost: React.FC = () => {
  const doc = (globalThis as any).document;
  const elRef = React.useRef<any>(null);
  if (doc && !elRef.current) {
    const el = doc.createElement('div');
    el.setAttribute('data-sk-toast-host', '');
    el.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483647;';
    elRef.current = el;
  }
  React.useEffect(() => {
    const el = elRef.current;
    if (!doc || !el) return;
    doc.body.appendChild(el);
    return () => { try { doc.body.removeChild(el); } catch { /* already gone */ } };
  }, [doc]);
  if (!elRef.current) return <RNToast config={toastConfig} />;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const ReactDOM = require('react-dom');
  return ReactDOM.createPortal(<RNToast config={toastConfig} />, elRef.current);
};

const Toast: React.FC = () => {
  if (Platform.OS !== 'web') return <RNToast config={toastConfig} />;
  return <WebToastHost />;
};

/* ──────────────────────────────────────────────────────────────────────────
 * Compatibility layer: ToastProvider + useToast (delegates to showToast).
 * Kept so existing `const toast = useToast()` call sites work unchanged.
 * ──────────────────────────────────────────────────────────────────────── */
export interface ToastOptions {
  tone?: ToastTone;
  title?: string;
  message?: string;
  duration?: number;
}

export interface ToastApi {
  (opts: ToastOptions | string): void;
  success: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => void;
  error: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => void;
  warn: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => void;
  info: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => void;
  dismiss: (id?: string) => void;
}

const api = ((opts: ToastOptions | string): void => {
  if (typeof opts === 'string') return showToast('success', opts);
  const text1 = opts.title ?? opts.message ?? '';
  const text2 = opts.title ? opts.message : undefined;
  showToast(opts.tone ?? 'success', text1, text2);
}) as ToastApi;

api.success = (msg, opts) => showToast('success', msg, opts?.message);
api.error = (msg, opts) => showToast('danger', msg, opts?.message);
api.warn = (msg, opts) => showToast('warn', msg, opts?.message);
api.info = (msg, opts) => showToast('info', msg, opts?.message);
api.dismiss = () => RNToast.hide();

/** Passthrough — the <Toast/> root lives in App.tsx. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export function useToast(): ToastApi {
  return api;
}

export { Toast };
export default Toast;
